import { FINAL_STATUSES, STATUS_SLUGS, STATUSES, TYPE_SLUGS } from '../constants/announcement';
import type {
  AnnouncementStatus,
  AnnouncementType,
  IAnnouncementEntity,
  IAnnouncementLocalization,
  TSort,
  TSortKey,
} from '../types/announcement';

export const statusSlug = (status: AnnouncementStatus): string => STATUS_SLUGS[status] ?? 'unknown';
export const typeSlug = (type: AnnouncementType): string => TYPE_SLUGS[type] ?? 'unknown';

export const defaultTitle = (a: IAnnouncementEntity): string =>
  (a.localizations.find(l => l.isDefault) ?? a.localizations[0])?.title ?? '';

/** The title in the admin's language when the announcement has it, else in its default language. */
export const localizedTitle = (a: IAnnouncementEntity, language: string): string =>
  a.localizations.find(l => l.language === language && l.title)?.title || defaultTitle(a);

/** Dotted versions compared number by number ("1.9" < "1.22"); rows without a version go first. */
export function compareVersions(a?: string, b?: string): number {
  const parts = (v?: string) => (v ?? '').split('.').filter(Boolean).map(Number);
  const [x, y] = [parts(a), parts(b)];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? -1) - (y[i] ?? -1);
    if (d) return d;
  }
  return 0;
}

/** Pinning from the list is for items that can still change; withdrawn and expired only show their mark. */
export const isPinnable = (a: IAnnouncementEntity): boolean => !FINAL_STATUSES.includes(a.status);

const time = (d?: Date) => d?.getTime() ?? 0;

/** The list rows: filtered by the URL filters, pinned first (as in the ZennoBrowser feed), then the column sort. */
export function visibleAnnouncements(
  items: IAnnouncementEntity[],
  options: {
    statusFilter: string[];
    typeFilter: string[];
    sort: TSort;
    typeLabel: (type: AnnouncementType) => string;
    locale: string;
  },
): IAnnouncementEntity[] {
  const { statusFilter, typeFilter, sort, typeLabel, locale } = options;
  const compare: Record<TSortKey, (a: IAnnouncementEntity, b: IAnnouncementEntity) => number> = {
    status: (a, b) => STATUSES.indexOf(a.status) - STATUSES.indexOf(b.status),
    type: (a, b) => typeLabel(a.type).localeCompare(typeLabel(b.type)),
    title: (a, b) => localizedTitle(a, locale).localeCompare(localizedTitle(b, locale)),
    updatedAt: (a, b) => time(a.updatedAt) - time(b.updatedAt),
    version: (a, b) => compareVersions(a.businessVersion, b.businessVersion),
  };
  return items
    .filter(
      a =>
        (!statusFilter.length || statusFilter.includes(statusSlug(a.status))) &&
        (!typeFilter.length || typeFilter.includes(typeSlug(a.type))),
    )
    .sort((a, b) => {
      const pinned = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
      if (pinned || !sort) return pinned;
      return compare[sort.key](a, b) * (sort.order === 'descend' ? -1 : 1);
    });
}

export const newLocalizations = (): IAnnouncementLocalization[] => [
  { language: 'ru', title: '', text: '', isDefault: true },
];

/** datetime-local value (the manager's local time) of an instant; '' when unset. */
export const toLocalInput = (date: Date | undefined): string =>
  date ? new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : '';

/** The instant of a datetime-local value (sent to the service in UTC); undefined when empty. */
export const fromLocalInput = (value: string): Date | undefined => (value ? new Date(value) : undefined);

/** Empty CTA inputs mean "no button" and are not sent. */
export const toRequestLocalizations = (localizations: IAnnouncementLocalization[]): IAnnouncementLocalization[] =>
  localizations.map(l => ({ ...l, ctaLabel: l.ctaLabel?.trim() || undefined, ctaUrl: l.ctaUrl?.trim() || undefined }));

/** What users of `language` get, as the feed picks it: their own row with a title and text, else the default row. */
export function pickLocalization(
  localizations: IAnnouncementLocalization[],
  language: string,
): IAnnouncementLocalization | undefined {
  const own = localizations.find(l => l.language === language && l.title.trim() && l.text.trim());
  return own ?? localizations.find(l => l.isDefault) ?? localizations[0];
}
